// Everything Submit learns about a picked file before upload, in one cooperative pass.
//   analyzeFile(file, cols, { signal?, onProgress? }) → Promise<Analysis>
//       reads the first 64 MiB once: PE headers, type, entropy portrait; SHA-256 over the WHOLE file
//       (crypto.subtle when the file fits the window and the page is a secure context, else the
//       streaming JS hasher in 8 MiB slices). Progress 0 → 1 drives the plate's scan band and "Hashing… 42%".
//   portraitOfFile(file, cols, opts) → Promise<Portrait>    redraw for another column count (container resize)
//   MAX_SAMPLE_BYTES   the controller reads at most 512 MiB of a sample (backend maxSampleBytes)
import { buildPortrait, makeTicker, PORTRAIT_MAX, type Portrait } from "./entropy";
import { describeType, parsePe, type PeInfo } from "./pe-sections";
import { hasSubtle, Sha256, sha256Hex } from "./sha256";

export const MAX_SAMPLE_BYTES = 512 * 1024 * 1024;
const HASH_SLICE = 8 * 1024 * 1024;

export type Analysis = {
  sha256: string;
  type: string;
  pe: PeInfo | null;
  portrait: Portrait;
};

type Opts = { signal?: AbortSignal; onProgress?: (fraction: number) => void };

async function head(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, Math.min(file.size, PORTRAIT_MAX)).arrayBuffer());
}

export async function portraitOfFile(file: File, cols: number, opts: Opts = {}): Promise<Portrait> {
  const bytes = await head(file);
  return buildPortrait(bytes, file.size, cols, parsePe(bytes), opts);
}

export async function analyzeFile(file: File, cols: number, opts: Opts = {}): Promise<Analysis> {
  const { signal, onProgress } = opts;
  const bytes = await head(file);
  if (signal?.aborted) throw new DOMException("aborted", "AbortError");
  const pe = parsePe(bytes);
  const type = describeType(bytes, file.name, pe);

  const native = hasSubtle() && file.size <= bytes.length;
  // the portrait pass reads `bytes` once; the JS hasher reads the whole file once more
  const portraitShare = native ? 0.92 : bytes.length / (bytes.length + file.size);
  const portrait = await buildPortrait(bytes, file.size, cols, pe, {
    signal,
    onProgress: (f) => onProgress?.(f * portraitShare),
  });

  let sha256: string;
  if (native) {
    sha256 = await sha256Hex(bytes);
  } else {
    const tick = makeTicker(signal);
    const h = new Sha256();
    const report = (done: number) => onProgress?.(portraitShare + (1 - portraitShare) * (done / Math.max(1, file.size)));
    for (let o = 0; o < bytes.length; o += HASH_SLICE) {
      h.update(bytes.subarray(o, Math.min(bytes.length, o + HASH_SLICE)));
      report(Math.min(bytes.length, o + HASH_SLICE));
      await tick();
    }
    for (let o = bytes.length; o < file.size; o += HASH_SLICE) {
      const chunk = new Uint8Array(await file.slice(o, Math.min(file.size, o + HASH_SLICE)).arrayBuffer());
      h.update(chunk);
      report(Math.min(file.size, o + HASH_SLICE));
      await tick();
    }
    sha256 = h.hex();
  }
  onProgress?.(1);
  return { sha256, type, pe, portrait };
}
